// ─── Constants ────────────────────────────────────────────────────────────────

export const TOTAL_WIZARD_STEPS = 8;

export const TEMPLATES = [
  { key: "invoiceIncoming" },
  { key: "invoiceOutgoing" },
  { key: "payroll" },
  { key: "vatReturn" },
  { key: "annualFinancial" },
] as const;

export type TemplateKey = (typeof TEMPLATES)[number]["key"];

// Maps a wizard template to the reused Process.type (Prisma ProcessType).
// The invoice-intake process (INVOICE_RECEIVE) is the pilot's mandatory
// minimum; the other four remain optional per A5a-request.md.
export const TEMPLATE_PROCESS_TYPE: Record<TemplateKey, string> = {
  invoiceIncoming: "INVOICE_RECEIVE",
  invoiceOutgoing: "INVOICE_SEND",
  payroll: "PAYROLL",
  vatReturn: "VAT_PRE_REGISTRATION",
  annualFinancial: "ANNUAL_FINANCIAL_STATEMENTS",
};

export const MOCK_DEPARTMENTS = [
  "Officemanagement",
  "Buchhaltung",
  "Geschäftsführung",
  "IT",
  "Vertrieb",
  "Steuerabteilung",
];

export const MOCK_SYSTEMS = [
  "DATEV Unternehmen Online",
  "DATEV Rechnungswesen",
  "Microsoft Outlook",
  "Microsoft Teams",
  "Microsoft Planner",
  "Personio",
  "Jira",
  "SharePoint",
];

export const AVAILABLE_INTERFACES = [
  "Rechnungseingang",
  "Rechnungsausgang",
  "Lohnabrechnung",
  "Monatsabschluss",
  "Jahresabschluss",
  "Umsatzsteuervoranmeldung und -jahreserklärung",
  "Betriebsprüfung (LSt, USt, KSt/GewSt)",
];

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ProcedureBuilderFormData {
  goal: string;
  responsibilities: {
    departments: string[];
    tasks: Record<string, string>;
  };
  systems: {
    selected: string[];
  };
  processStepsMode: "text" | "steps";
  processStepsText: string;
  processSteps: Array<{ title: string; description: string }>;
  risks: string[];
  controls: {
    preventive: Array<{ text: string; active: boolean }>;
    detective: Array<{ text: string; active: boolean }>;
  };
  documentation: string;
  interfaces: string[];
  additionalInterfaces: string;
}

// ─── Template data ────────────────────────────────────────────────────────────

export function getTemplateData(
  key: TemplateKey,
): Partial<ProcedureBuilderFormData> {
  const templates: Record<TemplateKey, Partial<ProcedureBuilderFormData>> = {
    invoiceIncoming: {
      goal: `Sicherstellung eines ordnungsgemäßen, vollständigen und revisionssicheren Rechnungseingangs­prozesses unter Beachtung der gesetzlichen Anforderungen (§§ 14, 14a UStG, GoBD).

Ziel des Prozesses ist:
- die vollständige Erfassung aller eingehenden Rechnungen
- die formelle und inhaltliche Prüfung der Rechnungen
- die zutreffende umsatzsteuerliche Beurteilung
- die fristgerechte Weiterverarbeitung zur Buchung und Zahlung
- die Vermeidung von Zahlungs-, Steuer- und GoBD-Risiken
- die lückenlose Dokumentation im Rahmen des internen Kontrollsystems (IKS / Tax CMS)`,
      responsibilities: {
        departments: ["Officemanagement", "Buchhaltung", "Geschäftsführung"],
        tasks: {
          Officemanagement: `- Übernahme des Uploads aller eingehenden Rechnungen in DATEV Unternehmen Online
- Sicherstellung der ordnungsgemäßen Belegablage im DATEV-Rechenzentrum
- Klassifizierung der Belege (Lieferant, Leistungsdatum, Rechnungsdatum, Betrag, Steuersatz)
- Bereitstellung der Belege bis zum Ende der jeweiligen Kalenderwoche`,
          Buchhaltung: `- Übernahme der Buchungsstapel aus DATEV Unternehmen Online
- Prüfung der Belege auf formelle Ordnungsmäßigkeit, Vollständigkeit und Plausibilität
- Prüfung und Vergabe der zutreffenden Vorsteuerschlüssel
- Verbuchung der Rechnungen im DATEV Rechnungswesen
- Einholung der Zahlungsfreigaben im Rahmen des wöchentlichen Meetings`,
          Geschäftsführung: `- Freigabe der Zahlungen im Rahmen des definierten Freigabeprozesses
- Entscheidung bei Zweifelsfällen oder sachlich/steuerlich kritischen Rechnungen`,
        },
      },
      systems: {
        selected: [
          "DATEV Unternehmen Online",
          "DATEV Rechnungswesen",
          "Microsoft Teams",
          "Microsoft Planner",
          "Microsoft Outlook",
        ],
      },
      processSteps: [
        {
          title: "Rechnungseingang",
          description:
            "Rechnungen gehen über das zentrale Rechnungseingangspostfach (invoice@…) ein und werden automatisch in DATEV Unternehmen Online hochgeladen. Papierbelege werden direkt eingescannt.",
        },
        {
          title: "Sachliche und formelle Prüfung / Vorerfassung",
          description:
            "Prüfung der sachlichen Richtigkeit sowie der formellen Rechnungsangaben (Adresse, Leistungsbeschreibung, Datum, Betrag). Bei Abweichungen Kontaktaufnahme mit dem Lieferanten.",
        },
        {
          title: "Buchung und steuerliche Prüfung",
          description:
            "Übernahme der Buchungsstapel aus DATEV Unternehmen Online. Prüfung auf Ordnungsmäßigkeit gemäß § 14 UStG. Erfassung von Sachkonten, Steuerschlüsseln und Kostenstellen.",
        },
      ],
      risks: [
        "Unzutreffende umsatzsteuerliche Behandlung von Eingangsrechnungen",
        "Zahlung auf nicht ordnungsgemäße oder ungeprüfte Rechnungen",
        "Unvollständige, verspätete oder nicht nachvollziehbare Belegerfassung",
        "Schwächen in Verantwortlichkeiten und Kontrollen",
      ],
      controls: {
        preventive: [
          {
            text: "Mehrstufiges 4-Augen-Prinzip (Officemanagement → Buchhaltung → Geschäftsführung)",
            active: false,
          },
          {
            text: "Erste Sicht- und Plausibilitätsprüfung der Belege durch das Officemanagement",
            active: false,
          },
          {
            text: "Finale fachliche und steuerliche Prüfung durch die Buchhaltung bei der Verbuchung",
            active: false,
          },
          {
            text: "Zahlungsfreigabe im wöchentlichen Meeting unter Einbeziehung der Geschäftsführung",
            active: false,
          },
        ],
        detective: [
          {
            text: "Abgleich der Steuerschlüssel im Rahmen der USt-Voranmeldung",
            active: false,
          },
          {
            text: "Protokollierung aller Bearbeitungsschritte in DATEV",
            active: false,
          },
          {
            text: "Stichprobenprüfungen und Auffälligkeitsprüfungen im Jahresabschluss",
            active: false,
          },
          {
            text: "Nachträgliche Identifikation fehlender oder unvollständiger Rechnungen",
            active: false,
          },
        ],
      },
      documentation: `- DATEV Unternehmen Online: Revisionssichere Ablage und Verknüpfung von Belegen und Buchungen
- DATEV: Automatische Beleg- und Aktivitätenprotokolle (Upload, Bearbeitung, Verbuchung, Benutzerzuordnung)
- Microsoft Planner: Nachverfolgung von Beleganforderungen und Rückfragen`,
      interfaces: [
        "Lohnabrechnung",
        "Monatsabschluss",
        "Jahresabschluss",
        "Umsatzsteuervoranmeldung und -jahreserklärung",
        "Betriebsprüfung (LSt, USt, KSt/GewSt)",
      ],
    },

    invoiceOutgoing: {
      goal: `Sicherstellung eines ordnungsgemäßen, vollständigen und fristgerechten Rechnungsausgangs unter Beachtung der gesetzlichen Anforderungen (insbesondere §§ 14, 14a UStG sowie GoBD).

Ziel des Prozesses ist:
- die vollständige und zeitnahe Erstellung aller Ausgangsrechnungen
- die korrekte inhaltliche und formelle Ausgestaltung der Rechnungen
- die zutreffende umsatzsteuerliche Behandlung
- die lückenlose Dokumentation und Nachvollziehbarkeit
- die ordnungsgemäße Übergabe an Buchhaltung und Zahlungsüberwachung
- die Minimierung von steuerlichen, rechtlichen und wirtschaftlichen Risiken`,
      responsibilities: {
        departments: ["Officemanagement", "Buchhaltung", "Geschäftsführung"],
        tasks: {
          Officemanagement: `- Sicherstellung der vollständigen Stundenbuchungen aller Mitarbeiter
- Kontrolle der Stunden auf Vollständigkeit`,
          Buchhaltung: `- Erstellung und Versand der Leistungsnachweise gemeinsam mit der GL
- Rechnungserstellung nach Kundenfreigabe im Auftragswesen Next
- Entscheidung über Umsatzart und Steuersätze
- Verbuchung der Rechnungen im Rechnungswesen
- Durchführung von Zahlungserinnerungen und Mahnungen`,
          Geschäftsführung: `- Erstellung von Angeboten für Neukunden
- Anlage der Rechnungstickets in Jira
- Upload unterschriebener Angebote
- Überwachung der Zahlungseingänge`,
        },
      },
      systems: {
        selected: [
          "DATEV Unternehmen Online",
          "DATEV Rechnungswesen",
          "Personio",
          "Microsoft Outlook",
          "Microsoft Teams",
          "Jira",
        ],
      },
      processSteps: [
        {
          title:
            "Prüfung der Zeiterfassung und Erstellung der Leistungsnachweise",
          description:
            "Sicherstellung der vollständigen Zeiterfassung aller abrechnungsrelevanten Stunden. Erstellung und Versand der Leistungsnachweise an den Kunden zur Freigabe.",
        },
        {
          title: "Rechnungsstellung anhand der Leistungsnachweise",
          description:
            "Nach Kundenfreigabe: Erstellung der Ausgangsrechnung im Auftragswesen Next. Festlegung der Umsatzart sowie der steuerlichen Behandlung. Versand der Rechnung.",
        },
        {
          title: "Monatliche Dauerrechnungen an Partnerunternehmen",
          description:
            "Monatliche Abrechnung vertraglich vereinbarter Pauschalen. Erstellung der Dauerrechnungen im Auftragswesen Next. Prüfung der steuerlichen Behandlung.",
        },
        {
          title: "Verbuchung und abschließende Prüfung",
          description:
            "Bereitstellung der Buchungsstapel über DATEV Unternehmen Online. Übernahme in DATEV Rechnungswesen. Verbuchung mit Umsatzart, Sachkonto und Kostenstellen. Vollständigkeitsabgleich.",
        },
      ],
      risks: [
        "Falsche umsatzsteuerliche Behandlung von Ausgangsrechnungen",
        "Abrechnung nicht zutreffender Leistungsumfänge",
        "Unzureichende Trennung von Erstellung, Prüfung und Freigabe",
        "Dokumentations- und Nachvollziehbarkeitsmängel",
      ],
      controls: {
        preventive: [
          {
            text: "Vollständigkeitskontrolle der Zeiterfassung durch das Officemanagement",
            active: false,
          },
          {
            text: "Abgleich der Leistungsnachweise mit Verträgen und Angeboten",
            active: false,
          },
          {
            text: "Trennung von Leistungserstellung, Rechnungsstellung und Verbuchung",
            active: false,
          },
          {
            text: "Systemgestützte Rechnungserstellung im Auftragswesen Next",
            active: false,
          },
        ],
        detective: [
          {
            text: "Abgleich Ausgangsrechnungen ↔ Verbuchung im DATEV Rechnungswesen",
            active: false,
          },
          {
            text: "Prüfung der Steuerschlüssel im Rahmen der USt-Voranmeldung",
            active: false,
          },
          {
            text: "Vollständigkeitsabgleich zwischen Leistungsnachweisen, Jira-Tickets und gebuchten Rechnungen",
            active: false,
          },
          {
            text: "Stichprobenprüfung im Jahresabschluss durch den Steuerberater",
            active: false,
          },
        ],
      },
      documentation: `- DATEV Auftragswesen Next: Rechnungsausgangsjournal mit Dokumentation von Erstellung, Versand und Archivierung
- DATEV Rechnungswesen: Übergabe- und Buchungsberichte, Verknüpfung der Buchungssätze mit Rechnungsbelegen
- Jira: Nachverfolgung von Beleganforderungen und Rückfragen`,
      interfaces: [
        "Monatsabschluss",
        "Jahresabschluss",
        "Umsatzsteuervoranmeldung und -jahreserklärung",
        "Betriebsprüfung (LSt, USt, KSt/GewSt)",
      ],
    },

    payroll: {
      goal: `Sicherstellung einer ordnungsgemäßen, vollständigen und fristgerechten Lohn- und Gehaltsabrechnung unter Beachtung der steuer- und sozialversicherungsrechtlichen Anforderungen.

Ziel des Prozesses ist:
- die korrekte und vollständige Abrechnung aller Arbeitsentgelte
- die zutreffende lohnsteuerliche und sozialversicherungsrechtliche Behandlung
- die fristgerechte Übermittlung der Lohnsteueranmeldungen
- die lückenlose Dokumentation und Nachvollziehbarkeit
- die Minimierung von steuerlichen und arbeitsrechtlichen Risiken`,
      responsibilities: {
        departments: ["Buchhaltung", "Geschäftsführung", "Officemanagement"],
        tasks: {
          Buchhaltung: `- Durchführung der monatlichen Lohn- und Gehaltsabrechnung
- Prüfung und Verbuchung der Lohnsteueranmeldungen
- Überprüfung der Sozialversicherungsbeiträge und -meldungen
- Abstimmung der Lohnkonten`,
          Geschäftsführung: `- Freigabe der Lohnabrechnungen
- Genehmigung von Sonderzahlungen und Gehaltsänderungen`,
          Officemanagement: `- Pflege der Personalstammdaten
- Erfassung und Weiterleitung von Änderungsmitteilungen (Ein-/Austritte, Gehaltsänderungen)`,
        },
      },
      systems: {
        selected: ["DATEV Unternehmen Online", "Personio", "Microsoft Teams"],
      },
      processSteps: [
        {
          title: "Datenerhebung und Stammdatenpflege",
          description:
            "Erfassung aller abrechnungsrelevanten Änderungen (Ein-/Austritte, Gehaltsänderungen, Sonderzahlungen) und Pflege der Personalstammdaten.",
        },
        {
          title: "Durchführung der Lohnabrechnung",
          description:
            "Monatliche Lohn- und Gehaltsabrechnung auf Basis der aktuellen Stamm- und Bewegungsdaten. Berechnung aller Abzüge und Zulagen.",
        },
        {
          title: "Prüfung und Freigabe",
          description:
            "Prüfung der Abrechnungsergebnisse, Plausibilitätskontrolle. Freigabe durch die Geschäftsführung.",
        },
        {
          title: "Übermittlung und Verbuchung",
          description:
            "Übermittlung der Lohnsteueranmeldung ans Finanzamt. Meldung der Sozialversicherungsbeiträge. Verbuchung der Lohnjournale.",
        },
      ],
      risks: [
        "Fehlerhafte lohnsteuerliche Behandlung von Gehaltsbestandteilen",
        "Verspätete oder fehlerhafte Lohnsteueranmeldungen",
        "Unzutreffende Einstufung in Steuer- oder SV-Klassen",
        "Fehlende oder unvollständige Personalunterlagen",
      ],
      controls: {
        preventive: [
          {
            text: "4-Augen-Prinzip bei der Freigabe der Lohnabrechnungen",
            active: false,
          },
          { text: "Systemgestützte Plausibilitätsprüfungen", active: false },
          {
            text: "Abstimmung der Stammdaten vor der Abrechnungsperiode",
            active: false,
          },
        ],
        detective: [
          {
            text: "Monatlicher Soll-Ist-Vergleich der Lohnkosten",
            active: false,
          },
          {
            text: "Prüfung der Lohnsteueranmeldungen im Rahmen der Jahresabschlussprüfung",
            active: false,
          },
          { text: "Protokollierung aller Stammdatenänderungen", active: false },
        ],
      },
      documentation: `- DATEV: Lohnjournale, Buchungsnachweise und Lohnsteueranmeldungen
- Personio: Personalakten und Gehaltshistorie
- Archivierung der Abrechnungsunterlagen gemäß GoBD`,
      interfaces: [
        "Monatsabschluss",
        "Jahresabschluss",
        "Betriebsprüfung (LSt, USt, KSt/GewSt)",
      ],
    },

    vatReturn: {
      goal: `Sicherstellung einer ordnungsgemäßen, vollständigen und fristgerechten Umsatzsteuervoranmeldung unter Beachtung der gesetzlichen Anforderungen (§§ 18, 18a UStG).

Ziel des Prozesses ist:
- die korrekte Ermittlung der Umsatzsteuer- und Vorsteuerbeträge
- die fristgerechte Übermittlung der Voranmeldung ans Finanzamt
- die zutreffende umsatzsteuerliche Behandlung aller Umsätze
- die lückenlose Dokumentation und Prüfbarkeit`,
      responsibilities: {
        departments: ["Buchhaltung", "Geschäftsführung"],
        tasks: {
          Buchhaltung: `- Ermittlung und Abstimmung der Umsatzsteuer- und Vorsteuerbeträge
- Erstellung und elektronische Übermittlung der USt-Voranmeldung
- Prüfung der Steuerschlüssel aller Buchungen
- Abstimmung der USt-Konten`,
          Geschäftsführung: `- Freigabe der Voranmeldung vor Übermittlung
- Ansprechpartner für das Finanzamt`,
        },
      },
      systems: {
        selected: ["DATEV Unternehmen Online", "DATEV Rechnungswesen"],
      },
      processSteps: [
        {
          title: "Vorbereitung und Kontenabstimmung",
          description:
            "Abstimmung aller umsatzsteuerrelevanten Konten. Prüfung der Steuerschlüssel auf Vollständigkeit und Richtigkeit.",
        },
        {
          title: "Ermittlung der Voranmeldungsbeträge",
          description:
            "Auswertung der Buchungen des Voranmeldungszeitraums. Ermittlung der abziehbaren Vorsteuer und der geschuldeten Umsatzsteuer.",
        },
        {
          title: "Erstellung und Übermittlung",
          description:
            "Erstellung der USt-Voranmeldung in DATEV. Freigabe durch die Geschäftsführung. Elektronische Übermittlung ans Finanzamt via ELSTER.",
        },
      ],
      risks: [
        "Falsche Zuordnung von Steuertatbeständen",
        "Verspätete Abgabe der Voranmeldung (Säumniszuschläge)",
        "Nicht abziehbare Vorsteuer aus formal mangelhaften Rechnungen",
        "Fehlerhafte Behandlung von innergemeinschaftlichen Erwerben oder Lieferungen",
      ],
      controls: {
        preventive: [
          {
            text: "Regelmäßige Prüfung der Steuerschlüssel bei der Buchung",
            active: false,
          },
          {
            text: "Kontenabstimmung vor Erstellung der Voranmeldung",
            active: false,
          },
          {
            text: "4-Augen-Prinzip bei Freigabe und Übermittlung",
            active: false,
          },
        ],
        detective: [
          {
            text: "Abgleich der Voranmeldungssummen mit den Buchhaltungskonten",
            active: false,
          },
          {
            text: "Vergleich mit den Vormonaten auf Plausibilität",
            active: false,
          },
          {
            text: "Prüfung im Rahmen der Jahressteuererklärung",
            active: false,
          },
        ],
      },
      documentation: `- DATEV: Voranmeldungsbelege und ELSTER-Übertragungsprotokolle
- Ablage aller Belege im DATEV-Rechenzentrum
- Dokumentation von Korrekturbuchungen`,
      interfaces: [
        "Rechnungseingang",
        "Rechnungsausgang",
        "Jahresabschluss",
        "Betriebsprüfung (LSt, USt, KSt/GewSt)",
      ],
    },

    annualFinancial: {
      goal: `Sicherstellung eines ordnungsgemäßen, vollständigen und fristgerechten handels- und steuerrechtlichen Jahresabschlusses unter Beachtung der gesetzlichen Anforderungen (HGB, EStG, GoBD).

Ziel des Prozesses ist:
- die vollständige und korrekte Erfassung aller Geschäftsvorfälle des Geschäftsjahres
- die zutreffende Bewertung aller Bilanzpositionen
- die fristgerechte Erstellung von Bilanz und Gewinn- und Verlustrechnung
- die lückenlose Dokumentation und Prüfbarkeit durch externe Prüfer`,
      responsibilities: {
        departments: ["Buchhaltung", "Geschäftsführung"],
        tasks: {
          Buchhaltung: `- Durchführung aller Abschlussbuchungen (Rückstellungen, Abgrenzungen, Abschreibungen)
- Abstimmung aller Konten
- Erstellung des Jahresabschlusses gemeinsam mit dem Steuerberater
- Bereitstellung aller Unterlagen für den Steuerberater`,
          Geschäftsführung: `- Freigabe des Jahresabschlusses
- Unterzeichnung der Bilanzen und Steuererklärungen
- Abstimmung mit dem Steuerberater`,
        },
      },
      systems: {
        selected: [
          "DATEV Unternehmen Online",
          "DATEV Rechnungswesen",
          "Microsoft Teams",
        ],
      },
      processSteps: [
        {
          title: "Vorbereitung und Kontenabstimmung",
          description:
            "Vollständige Abstimmung aller Konten. Klärung offener Posten. Prüfung auf fehlende Belege.",
        },
        {
          title: "Abschlussbuchungen",
          description:
            "Erfassung aller Abgrenzungen, Rückstellungen und Abschreibungen. Bewertung der Bilanzpositionen gemäß HGB.",
        },
        {
          title: "Erstellung und Prüfung",
          description:
            "Erstellung von Bilanz und GuV. Abstimmung mit dem Steuerberater. Plausibilitätsprüfungen und Vergleich mit dem Vorjahr.",
        },
        {
          title: "Freigabe und Einreichung",
          description:
            "Freigabe durch die Geschäftsführung. Einreichung beim Finanzamt und ggf. Offenlegung beim Bundesanzeiger.",
        },
      ],
      risks: [
        "Unvollständige oder fehlerhafte Erfassung von Geschäftsvorfällen",
        "Falsche Bewertung von Bilanzpositionen",
        "Verspätete Erstellung (Ordnungsgeldrisiko)",
        "Fehlende oder unzureichende Dokumentation",
      ],
      controls: {
        preventive: [
          {
            text: "Vollständige Kontenabstimmung vor Abschlussbeginn",
            active: false,
          },
          { text: "Abstimmung der Jahresabschlusscheckliste", active: false },
          {
            text: "4-Augen-Prinzip bei allen Abschlussbuchungen",
            active: false,
          },
        ],
        detective: [
          { text: "Prüfung durch den Steuerberater", active: false },
          { text: "Vergleich mit dem Vorjahresabschluss", active: false },
          { text: "Stichprobenprüfungen durch externe Prüfer", active: false },
        ],
      },
      documentation: `- DATEV: Jahresabschlussunterlagen, Buchungsnachweise, Kontensalden
- Ablage aller Abschlussunterlagen im DATEV-Rechenzentrum gemäß GoBD (10-jährige Aufbewahrungsfrist)
- Dokumentation aller Abschlussbuchungen mit Belegnachweisen`,
      interfaces: [
        "Rechnungseingang",
        "Rechnungsausgang",
        "Lohnabrechnung",
        "Umsatzsteuervoranmeldung und -jahreserklärung",
        "Betriebsprüfung (LSt, USt, KSt/GewSt)",
      ],
    },
  };

  return templates[key];
}
